import { ConfigFieldType, FieldStatus, TextInputFieldData, RulesRHF } from 'adapter/in/common/form/types';
import useTextContainer from 'adapter/in/common/form/Text/useTextContainer';
import FormInputText from 'adapter/in/common/components/Text';
import PresetFormFieldProperties from 'domain/entity/preset/form/presetFormFieldProperties';
import React, { ReactElement } from 'react';

const THRESHOLD_INPUT_TEXTAREA = 100;
const EMPTY_VALUE = '';

const getControlInput = (properties?: PresetFormFieldProperties): string =>
    (properties?.maxLength || 0) > THRESHOLD_INPUT_TEXTAREA ? 'Textarea' : 'Input';

type TextInputContainerProps = {
    name: string;
    label: string;
    configType: ConfigFieldType;
    footer: string;
    dataDependsOn?: string[];
    defaultValue?: TextInputFieldData;
    properties?: PresetFormFieldProperties;
    rules?: RulesRHF;
};

const clear = (initialValue: string, configType: ConfigFieldType) => buildTextInputFieldData(FieldStatus.RESET, initialValue, configType);
const reset = (initialValue: string, configType: ConfigFieldType) =>
    buildTextInputFieldData(FieldStatus.INITIAL_VALUE, initialValue, configType, initialValue);

export const buildTextInputFieldData = (
    status: FieldStatus,
    initialValue = EMPTY_VALUE,
    configType: ConfigFieldType = ConfigFieldType.DATA,
    value = EMPTY_VALUE
): TextInputFieldData => ({
    status,
    initialValue,
    value,
    serialize: () => value,
    hasValue: () => value !== EMPTY_VALUE,
    getInitialValue: () => initialValue,
    clear: () => clear(initialValue, configType),
    reset: () => reset(initialValue, configType),
    type: configType,
});

const TextInputContainer = ({
    name,
    label,
    configType,
    footer,
    properties,
    dataDependsOn,
    defaultValue,
    rules,
}: TextInputContainerProps): ReactElement => {
    const { value, handleOnChange, invalid, error } = useTextContainer(name, configType, rules, dataDependsOn, defaultValue);
    return (
        <FormInputText
            control={getControlInput(properties)}
            name={name}
            label={label}
            properties={properties}
            onChange={handleOnChange}
            isLoading={false}
            error={invalid}
            errorMessage={error}
            dataTestId={getControlInput(properties).toLowerCase()}
            footer={footer}
            value={value?.status === FieldStatus.INITIAL_VALUE ? value.getInitialValue() : value?.value}
        />
    );
};

export default TextInputContainer;
